import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, CircleNotch } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import Reveal from "@/components/site/Reveal";
import { submitInquiry } from "@/lib/api";

const SERVICES = [
  "Cased-Hole Logging",
  "Perforating",
  "Production Logging",
  "Pipe Recovery",
  "Well Integrity",
  "Offshore Operations",
  "Sales — Electrical Appliances",
  "Sales — Electrical Equipment",
  "Sales — Cable Installation Kit",
  "Sales — Electrical Services",
  "Other / Not sure",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  "rounded-none h-12 border-white/15 bg-transparent px-4 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!EMAIL_RE.test(form.email)) return toast.error("Please enter a valid email.");
    if (form.message.trim().length < 5)
      return toast.error("Tell us a little about the job.");

    setLoading(true);
    try {
      await submitInquiry({
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Request received. Our operations desk will be in touch.");
      setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-background py-24 md:py-40"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <span className="mono-label text-primary">/ Contact</span>
            <h2 className="mt-6 font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.85]">
              Request
              <br />a job<span className="text-primary">.</span>
            </h2>
            <p className="mt-8 text-foreground/60 leading-relaxed max-w-md">
              Tell us the well, the water and the window. Our operations desk will scope the
              wireline program and get a certified crew moving.
            </p>

            <div className="mt-12 space-y-6 border-t border-white/10 pt-8">
              <div>
                <p className="mono-label text-muted-foreground">Operations Desk</p>
                <a
                  href="tel:+16263863529"
                  className="block mt-1 font-mono-tech text-lg link-underline"
                  data-testid="contact-phone"
                >
                  +1 (626) 386-3529
                </a>
                <a
                  href="tel:+18035791310"
                  className="block mt-1 font-mono-tech text-lg link-underline"
                  data-testid="contact-phone-2"
                >
                  +1 (803) 579-1310
                </a>
              </div>
              <div>
                <p className="mono-label text-muted-foreground">Email</p>
                <a
                  href="mailto:order@bandcsystem.com"
                  className="block mt-1 font-mono-tech text-lg link-underline"
                  data-testid="contact-email"
                >
                  order@bandcsystem.com
                </a>
              </div>
              <div>
                <p className="mono-label text-muted-foreground">Address</p>
                <p className="mt-1 font-mono-tech text-lg">
                  114 Adger Rd
                  <br />
                  Pendleton, SC 29670, US
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            {done ? (
              <div
                className="border border-primary/40 bg-secondary/40 p-10 md:p-16 h-full flex flex-col justify-center"
                data-testid="contact-success"
              >
                <span className="mono-label text-primary">/ Received</span>
                <h3 className="mt-6 font-display font-bold uppercase tracking-tight text-3xl md:text-4xl">
                  Your request is in the queue.
                </h3>
                <p className="mt-4 text-foreground/60 max-w-md">
                  Thanks — our operations desk has your details and will respond shortly.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="mt-8 self-start mono-label border border-white/20 px-6 py-3 hover:border-primary hover:text-primary transition-colors"
                  data-testid="contact-reset"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="border border-white/10 p-6 md:p-10"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="mono-label text-muted-foreground">Name *</label>
                    <Input
                      className={inputCls}
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Jane Doe"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="mono-label text-muted-foreground">Company</label>
                    <Input
                      className={inputCls}
                      value={form.company}
                      onChange={set("company")}
                      placeholder="Operator / Service Co."
                      data-testid="contact-input-company"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="mono-label text-muted-foreground">Email *</label>
                    <Input
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@company.com"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="mono-label text-muted-foreground">Phone</label>
                    <Input
                      className={inputCls}
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+1 000 000 0000"
                      data-testid="contact-input-phone"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-5">
                  <label className="mono-label text-muted-foreground">Service needed</label>
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger
                      className={`${inputCls} data-[placeholder]:text-muted-foreground/60`}
                      data-testid="contact-select-service"
                    >
                      <SelectValue placeholder="Select a service line" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-white/15 bg-secondary text-foreground">
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          className="rounded-none font-mono-tech text-sm"
                          data-testid={`contact-service-opt-${s
                            .toLowerCase()
                            .replace(/[^a-z]+/g, "-")
                            .replace(/^-|-$/g, "")}`}
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2 mt-5">
                  <label className="mono-label text-muted-foreground">Job details *</label>
                  <Textarea
                    className={`${inputCls} h-auto min-h-[130px] py-3`}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Well type, depth, location, target window..."
                    data-testid="contact-input-message"
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.98 }}
                  className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 mono-label hover:bg-foreground hover:text-background transition-colors duration-200 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      Sending
                      <CircleNotch size={18} weight="bold" className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Submit Request
                      <ArrowUpRight size={18} weight="bold" />
                    </>
                  )}
                </motion.button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
